/* eslint-disable consistent-return */

/* eslint-disable default-case */
import { useStateManagerContext } from "../../context/stateManager";
import { fetchCountry } from "../../utils/fetchCountry";
import { LocalContentProps } from "./types";
import { School, Scores, POI, POIs, Schools } from "@local-logic/client";
import { baseStyles } from "@local-logic/core/ui";
import {
  type ScoreCategories,
  scoreCategories,
} from "@local-logic/elements-react";
import { FeatureCollection } from "@turf/turf";
import React, {
  useContext,
  useState,
  useMemo,
  useReducer,
  SetStateAction,
  useEffect,
} from "react";
import errorTracker from "~/utils/errorTracker";

interface Location {
  label?: string;
  lat: number;
  lng: number;
}

export type DefaultContext = {
  country: string | "";
  setCountry: (country: string | "") => void;
  activeScore: keyof Scores["location"] | "";
  setActiveScore: (score: keyof Scores["location"] | "") => void;
  hasFilteredPOIs?: boolean;
  setHasFilteredPOIs: React.Dispatch<SetStateAction<boolean>>;
  activeCategory: ScoreCategories;
  setActiveCategory: (value: ScoreCategories) => void;
  activePOI: POI | School | undefined;
  setActivePOI: (poi: POI | School | undefined) => void;
  hoveredPOI: POI | School | undefined;
  setHoveredPOI: (poi: POI | School | undefined) => void;
  commuteGeojson: FeatureCollection | google.maps.LatLng[] | undefined;
  setCommuteGeojson: (
    geojson: FeatureCollection | google.maps.LatLng[] | undefined
  ) => void;
  destination: Location | undefined;
  setDestination: (data: Location | undefined) => void;
  showCommuteCalculator?: boolean;
  setShowCommuteCalculator: React.Dispatch<SetStateAction<boolean>>;
  isInputAutoFocused?: boolean;
  setIsInputAutoFocused: React.Dispatch<SetStateAction<boolean>>;
};

const LocalContentContext = React.createContext<DefaultContext>(
  {} as DefaultContext
);
const useLocalContent = () => useContext(LocalContentContext);

interface ScoreCategory {
  activeCategory: ScoreCategories;
  activeScoreByCategory: {
    [key in ScoreCategories]: keyof Scores["location"] | "";
  };
}

const scoreCategoryReducer = (
  state: ScoreCategory,
  action:
    | { type: "CATEGORY"; value: ScoreCategories }
    | { type: "SCORE"; value: keyof Scores["location"] | "" }
) => {
  switch (action.type) {
    case "CATEGORY": {
      return {
        ...state,
        activeCategory: action.value,
      };
    }

    case "SCORE": {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const newActiveCategory = scoreCategories.find((c) => {
        if (action.value === "") {
          return c.value === state.activeCategory;
        }

        return c.scores.includes(action.value);
      })!.value;

      return {
        activeCategory: newActiveCategory,
        activeScoreByCategory: {
          ...state.activeScoreByCategory,
          [newActiveCategory]: action.value,
        },
      };
    }
  }
};

const LocalContentProvider = ({ children }: { children: React.ReactNode }) => {
  const { sdkState } = useStateManagerContext<LocalContentProps>();

  const scoreCategoryDefault: ScoreCategory =
    window.innerWidth < baseStyles.screenSizes.tablet
      ? {
          activeCategory: "commute",
          activeScoreByCategory: {
            commute: "transit_friendly",
            amenities: "groceries",
            character: "quiet",
            nature: "parks",
            education: "primary_schools",
            wellness: "wellness",
          },
        }
      : {
          activeCategory: "commute",
          activeScoreByCategory: {
            commute: "",
            amenities: "",
            character: "",
            nature: "",
            education: "",
            wellness: "",
          },
        };

  const [scoreCategory, setScoreCategory] = useReducer(
    scoreCategoryReducer,
    scoreCategoryDefault
  );

  const [activePOI, setActivePOI] = useState<POI | School>();
  const [hasFilteredPOIs, setHasFilteredPOIs] = useState<boolean>(false);
  const [hoveredPOI, setHoveredPOI] = useState<POI | School>();
  const [commuteGeojson, setCommuteGeojson] = useState<
    FeatureCollection | google.maps.LatLng[]
  >();
  const [destination, setDestination] = useState<Location>();
  const [showCommuteCalculator, setShowCommuteCalculator] =
    useState<boolean>(false);
  const [isInputAutoFocused, setIsInputAutoFocused] = useState<boolean>(false);

  const [country, setCountry] = useState<string>("CAN");

  useEffect(() => {
    const getCountry = async () => {
      const getCountryResponse = await fetchCountry({
        lat: sdkState.options?.lat,
        lng: sdkState.options?.lng,
      });

      setCountry(getCountryResponse);
    };

    getCountry();
  }, []);

  const { activeCategory } = scoreCategory;
  const activeScore = scoreCategory.activeScoreByCategory[activeCategory];

  const setActiveScore = (value: keyof Scores["location"] | "") => {
    setScoreCategory({ type: "SCORE", value });
    errorTracker.addBreadCrumb(
      "localContent",
      `Active score changed to: ${value}`
    );
  };

  const setActiveCategory = (value: ScoreCategories) => {
    setScoreCategory({ type: "CATEGORY", value });
    errorTracker.addBreadCrumb(
      "localContent",
      `Active category changed to: ${value}`
    );
  };

  const contextValue = useMemo(
    () => ({
      country,
      setCountry,
      activeScore,
      setActiveScore,
      activeCategory,
      setActiveCategory,
      activePOI,
      setActivePOI,
      hoveredPOI,
      setHoveredPOI,
      commuteGeojson,
      setCommuteGeojson,
      destination,
      setDestination,
      showCommuteCalculator,
      isInputAutoFocused,
      setIsInputAutoFocused,
      setShowCommuteCalculator,
      hasFilteredPOIs,
      setHasFilteredPOIs,
    }),
    [
      country,
      setCountry,
      activeScore,
      setActiveScore,
      activeCategory,
      setActiveCategory,
      activePOI,
      setActivePOI,
      hoveredPOI,
      setHoveredPOI,
      commuteGeojson,
      setCommuteGeojson,
      destination,
      setDestination,
      showCommuteCalculator,
      isInputAutoFocused,
      setIsInputAutoFocused,
      setShowCommuteCalculator,
      hasFilteredPOIs,
      setHasFilteredPOIs,
    ]
  );

  return (
    <LocalContentContext.Provider value={contextValue}>
      {children}
    </LocalContentContext.Provider>
  );
};

export { useLocalContent, LocalContentProvider, type Location };
